import {
  Passkeys,
  ErrorCode,
  type CreatePasskeyOptions,
  type GetPasskeyOptions,
} from "@capawesome/capacitor-passkeys";
import { authClient } from "./auth-client.ts";

/**
 * A passkey is offered only where the device can actually show one
 * (`08-pos-terminal-and-qr.md` § B). Feature detection rather than a
 * catalogue flag: this is about what the device in front of somebody
 * supports, not what the merchant bought. Asked of the native passkey
 * plugin, which runs the ceremony on every platform (`#268`) — the web
 * implementation answers `isUserVerifyingPlatformAuthenticatorAvailable()`,
 * so a desktop browser with only a security key stops offering one.
 */
export async function passkeySupported(): Promise<boolean> {
  try {
    return (await Passkeys.isAvailable()).available;
  } catch {
    return false;
  }
}

export type PasskeyOutcome = "added" | "cancelled" | "stale" | "failed";

/**
 * What `addPasskey()`'s result means for the home screen. A separate function
 * so the mapping is testable without a WebAuthn device: `ERROR_CEREMONY_ABORTED`
 * and `AUTH_CANCELLED` are Better Auth's own cancel codes, `ErrorCode.Canceled`
 * ("CANCELED") is the native passkey plugin's, and `SESSION_NOT_FRESH` (or any
 * 403, since that is the status Better Auth's `freshSessionMiddleware` answers
 * with) means the session is more than 24 hours old and must be renewed first.
 */
export function passkeyOutcome(result: {
  error: { code?: string; status?: number } | null;
}): PasskeyOutcome {
  if (!result.error) return "added";
  const { code, status } = result.error;
  if (code === "ERROR_CEREMONY_ABORTED" || code === "AUTH_CANCELLED" || code === ErrorCode.Canceled)
    return "cancelled";
  if (code === "SESSION_NOT_FRESH" || status === 403) return "stale";
  return "failed";
}

/**
 * A plugin rejection's error code. Where it sits depends on the platform: a
 * native rejection (iOS, Android) puts it on `error.code`; the web
 * implementation throws a `CapacitorException`, which carries no `code` of
 * its own and puts it on `error.data.code` instead.
 */
export function pluginErrorCode(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const code = (error as { code?: unknown }).code;
  if (typeof code === "string") return code;
  const data = (error as { data?: { code?: unknown } }).data;
  return typeof data?.code === "string" ? data.code : undefined;
}

export async function addPasskey(): Promise<PasskeyOutcome> {
  try {
    const generated = await authClient.$fetch<CreatePasskeyOptions>(
      "/passkey/generate-register-options",
      { method: "GET", throw: false },
    );
    if (generated.error) return passkeyOutcome({ error: generated.error });

    let created;
    try {
      created = await Passkeys.createPasskey(generated.data);
    } catch (caught) {
      return passkeyOutcome({ error: { code: pluginErrorCode(caught) } });
    }

    const verified = await authClient.$fetch<unknown>("/passkey/verify-registration", {
      method: "POST",
      body: { response: created },
      throw: false,
    });
    return passkeyOutcome(verified);
  } catch {
    return "failed";
  }
}

/**
 * Network failures propagate to the caller — `sign-in.tsx`'s own catch is
 * what shows `staff.signIn.unreachable`. Only the plugin's own rejection is
 * handled here, since that one has a cancel to tell apart from a failure.
 */
export async function signInWithPasskey(): Promise<"signed-in" | "cancelled" | "failed"> {
  const generated = await authClient.$fetch<GetPasskeyOptions>(
    "/passkey/generate-authenticate-options",
    { method: "GET", throw: false },
  );
  if (generated.error) return "failed";

  let authenticated;
  try {
    authenticated = await Passkeys.getPasskey(generated.data);
  } catch (caught) {
    return pluginErrorCode(caught) === ErrorCode.Canceled ? "cancelled" : "failed";
  }

  const verified = await authClient.$fetch<unknown>("/passkey/verify-authentication", {
    method: "POST",
    body: { response: authenticated },
    throw: false,
  });
  return verified.error ? "failed" : "signed-in";
}
